import { type ReactElement, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { PromotionLayoutsProvider, setItem } from '@storefront/lib-global';
import { useInstantSearchContext } from '@storefront/lib-global/context/instantSearchContext';
import { FilterMenu } from '@storefront/lib-global/globalComponents/algolia/filter/FilterMenu';
import { AppliedFilters } from '@storefront/lib-global/globalComponents/algolia/filter/appliedFilters/AppliedFilters';
import { useAlgoliaAppliedFilter } from '@storefront/lib-global/hooks/useAlgoliaAppliedFilters';
import { useAlgoliaFilter } from '@storefront/lib-global/hooks/useAlgoliaFilter';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { media } from '@storefront/lib-global/stylings';
import { getCtLocale } from '@storefront/lib-global/utils/internationalization';
import { NoSearchResults } from '../../../product-search/components/noSearchResults/NoSearchResults';
import type { PLPLayoutProps } from '../PLPLayout';
import { QuickLinks } from '../QuickLinks';
import { ProductListBreadcrumb } from '../productListBreadcrumb/ProductListBreadcrumb';
import ProductListingHits from '../productListingHits/ProductListingHits';
import {
  CategoryName,
  Container,
  MobileContainer,
  ProductFilterContainer,
  TopContainer,
  Wrapper,
} from './ProductList.styles';
import { SlotContentPLP } from './SlotContentPLP';

/* eslint-disable promise/prefer-await-to-then */
const MobileSortByMenu = dynamic(
  () =>
    import('@storefront/lib-global/globalComponents/algolia/sortBy/MobileSortByMenu').then(
      (mod) => mod.MobileSortByMenu,
    ),
  { ssr: false },
);

const MobileFilterMenu = dynamic(
  () =>
    import('@storefront/lib-global/globalComponents/algolia/filter/MobileFilterMenu').then(
      (mod) => mod.MobileFilterMenu,
    ),
  { ssr: false },
);

const SortByMenu = dynamic(
  () => import('@storefront/lib-global/globalComponents/algolia/sortBy/SortByMenu').then((mod) => mod.SortByMenu),
  { ssr: false },
);
/* eslint-disable promise/prefer-await-to-then */

export function ProductList({ category, plpLayout, handlebarComponents, isMobile }: PLPLayoutProps): ReactElement {
  const isDesktop = useMediaMatch(media.greaterThan('lg'), !isMobile);
  const {
    slotContent = null,
    initialQuickLinks = 4,
    facetSequence = [],
    noSearchResultSlot = null,
    promotionLayouts,
  } = { ...plpLayout };
  const { locale } = useRouter();
  const { clearAll, currentRefinements } = useAlgoliaAppliedFilter();
  const { priceRange } = useAlgoliaFilter(getCtLocale(locale));
  const { name: categoryName, description: categoryDescription, ancestors, linkedCategories, h1Text } = category;

  const { results } = useInstantSearchContext();

  useEffect(() => {
    setItem('queryID', results?.queryID as string);
  }, [results?.queryID]);

  const noSearchResult = results?.nbHits === 0;

  const slotContentPLPProps = {
    isDesktop,
    slotContent,
    handlebarComponents,
    categoryDescription,
    locale,
  };

  if (noSearchResult) {
    return (
      <Wrapper>
        <NoSearchResults slotContent={noSearchResultSlot} handlebarComponents={handlebarComponents} />
        <PromotionLayoutsProvider promotionLayouts={promotionLayouts}>
          <SlotContentPLP {...slotContentPLPProps} />
        </PromotionLayoutsProvider>
      </Wrapper>
    );
  }

  return (
    <PromotionLayoutsProvider promotionLayouts={promotionLayouts}>
      <Wrapper>
        <TopContainer>
          <ProductListBreadcrumb ancestors={ancestors || []} category={category} hits={results?.nbHits} />
          {isDesktop && <SortByMenu />}
        </TopContainer>
        {!isDesktop && (h1Text || categoryName) && (
          <CategoryName data-testid="categoryName" aria-label={h1Text || categoryName || ''}>
            {h1Text || categoryName}
          </CategoryName>
        )}
        <Container>
          <ProductFilterContainer data-testid="filterContainer">
            {linkedCategories && linkedCategories.length > 0 && (
              <QuickLinks linkedCategories={linkedCategories} initialQuickLinks={initialQuickLinks} />
            )}
            {!isDesktop ? (
              <>
                <MobileContainer>
                  <MobileSortByMenu />
                  <MobileFilterMenu productCount={results?.nbHits ?? 0} facetSequence={facetSequence} />
                </MobileContainer>
                {!!currentRefinements.length && (
                  <AppliedFilters priceRange={priceRange} items={currentRefinements} clearAll={clearAll} />
                )}
              </>
            ) : (
              <FilterMenu productCount={results?.nbHits ?? 0} facetSequence={facetSequence} />
            )}
          </ProductFilterContainer>
          <ProductListingHits category={category} handlebarComponents={handlebarComponents} isMobile={isMobile} />
        </Container>
      </Wrapper>
      <SlotContentPLP {...slotContentPLPProps} />
    </PromotionLayoutsProvider>
  );
}

export default ProductList;
